import React, { useState, useEffect } from 'react';
import { useOrganization } from '@clerk/clerk-react';
import ClipLoader from 'react-spinners/ClipLoader';
import { useFetch } from './api';
import HomepageDashboard from './HomepageDashboard';
import HomepageWelcome from './HomepageWelcome';
import TrialBanner from './TrialBanner';

function Homepage(props) {
  const trackstar = useFetch();
  const { organization } = useOrganization();
  const [apiKeys, setApiKeys] = useState(null);

  const {
    connectionData, connectionsComplete, connectionsError, retryConnections,
    integrationLists, integrationListsLoading, orgInfo,
  } = props;

  // derived in render (not an effect) so it can never lag a frame behind
  // connectionData and flash the welcome page. null until the first page loads.
  let onboardComplete = null;
  if (connectionData !== null) {
    /* eslint-disable camelcase */
    const hasSandbox = connectionData.some(({ integration_name }) => integration_name === 'sandbox');
    const hasProdApiCall = connectionData.some(({ integration_name, times_used }) => integration_name !== 'sandbox' && times_used > 0);
    /* eslint-enable camelcase */
    onboardComplete = hasSandbox && hasProdApiCall;
  }

  useEffect(() => {
    if (organization) {
      trackstar('internal/api-keys').then((response) => {
        const { data } = response;
        setApiKeys(data);
      });
    }
  }, []);

  // the connection load is settled once it fully succeeds or fails; a failure
  // must reach a terminal state instead of spinning forever.
  const connectionsSettled = connectionsComplete || connectionsError;
  // a failure that left us without the full list. the dashboard's connection
  // counts would be truncated, so we show an error and retry rather than the
  // dashboard. a still-streaming successful load is not a failure.
  const connectionsFailed = connectionsError && !connectionsComplete;

  // show the dashboard as soon as we know the org is onboarded, streaming the
  // rest of the connections in. only fall through to the welcome page once the
  // full list has loaded, so a partial or mid-load list never flashes welcome.
  const stillLoading = (connectionData === null && !connectionsError)
    || integrationListsLoading
    || (!onboardComplete && !connectionsSettled);
  // never render the dashboard from a failed partial load: its counts would
  // look final while missing whatever the failed pages held.
  const showDashboard = !stillLoading && onboardComplete && !connectionsFailed;
  const showConnectionsError = !stillLoading && !showDashboard && connectionsFailed;

  const RANGES = [
    { key: '1d', days: 1 },
    { key: '7d', days: 7 },
    { key: '30d', days: 30 },
  ];
  const [range, setRange] = useState('7d');
  const { days } = RANGES.find((r) => r.key === range);

  let content;
  if (stillLoading) {
    content = (
      <div className="flex-1 flex items-center justify-center">
        <ClipLoader size={24} color="#D4532A" />
      </div>
    );
  } else if (showDashboard) {
    content = (
      <HomepageDashboard
        connectionData={connectionData}
        connectionsLoading={!connectionsComplete}
        integrationLists={integrationLists}
        days={days}
      />
    );
  } else if (showConnectionsError) {
    content = (
      <div className="flex-1 flex flex-col items-center justify-center text-center gap-3">
        <p className="text-sm text-surface-600">We couldn&apos;t load your connections.</p>
        <button
          type="button"
          onClick={retryConnections}
          className="px-3 py-1.5 text-xs font-semibold rounded-md bg-surface-900 text-surface-0 hover:bg-surface-800 transition-colors"
        >
          Try again
        </button>
      </div>
    );
  } else {
    content = <HomepageWelcome connectionData={connectionData} apiKeys={apiKeys} />;
  }

  return (
    <div className="page-container">
      <div className="flex items-center justify-between gap-4">
        <h1 className="page-title">
          Welcome back,
          {' '}
          <span className="text-surface-600">{organization.name.charAt(0).toUpperCase() + organization.name.slice(1)}</span>
        </h1>
        {showDashboard ? (
          <div className="flex items-center bg-surface-100 rounded-lg p-0.5 flex-shrink-0">
            {RANGES.map((r) => (
              <button
                key={r.key}
                type="button"
                onClick={() => setRange(r.key)}
                className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${
                  range === r.key ? 'bg-surface-0 text-surface-900 shadow-xs' : 'text-surface-500 hover:text-surface-800'
                }`}
              >
                {`${r.days}d`}
              </button>
            ))}
          </div>
        ) : null}
      </div>
      <TrialBanner org={orgInfo} />
      {content}
    </div>
  );
}

export default Homepage;
